import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { 
  Target, 
  TrendingUp, 
  Users, 
  BarChart3, 
  FileText, 
  Lightbulb,
  ArrowRight,
  CheckCircle
} from "lucide-react";

const Services = () => {
  const mainServices = [
    {
      title: "Service Mobile à Domicile",
      description: "Nous nous déplaçons directement chez vous pour effectuer le contrôle technique de vos véhicules légers.",
      icon: Target,
      features: [
        "Déplacement à votre domicile ou bureau",
        "Contrôle périodique voitures/taxis/utilitaires",
        "Rapport clair et transparent",
        "Suivi électronique du carnet"
      ],
      price: "Sur devis"
    },
    {
      title: "Poids Lourds Mobile",
      description: "Service mobile spécialisé pour le contrôle technique des camions, bus et véhicules de transport.",
      icon: TrendingUp,
      features: [
        "Déplacement sur site professionnel",
        "Vérification normes environnementales",
        "Rapport détaillé et transparent",
        "Contre-visite incluse si nécessaire"
      ],
      price: "Sur devis"
    },
    {
      title: "Deux-Roues Mobile",
      description: "Service mobile pour le contrôle technique des motos et scooters, directement chez vous.",
      icon: Users,
      features: [
        "Service à domicile pour motos/scooters",
        "Pré-visite pour détecter les défauts",
        "Rapport clair et transparent",
        "Suivi électronique du carnet"
      ],
      price: "Sur devis"
    }
  ];

  const additionalServices = [
    {
      title: "Pré-visite",
      description: "Détection des points à corriger avant la visite officielle pour éviter un refus.",
      icon: BarChart3,
    },
    {
      title: "Contre-visite",
      description: "Vérification des réparations effectuées suite à un contrôle initial.",
      icon: FileText,
    },
    {
      title: "Normes Environnementales",
      description: "Vérification de la conformité aux normes environnementales en vigueur.",
      icon: Lightbulb,
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <Badge variant="secondary" className="w-fit mx-auto">
              Nos Services
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Service Mobile de Contrôle Technique
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nous venons directement chez vous, partout au Sénégal, pour effectuer 
              le contrôle technique de tous types de véhicules selon les normes en vigueur.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services principaux
            </h2>
            <p className="text-xl text-gray-600">
              Des expertises clés pour transformer votre entreprise.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {mainServices.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow border-2 hover:border-primary/20">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">{service.title}</CardTitle>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t pt-6">
                    <div className="text-center space-y-4">
                      <div className="text-2xl font-bold text-primary">
                        {service.price}
                      </div>
                      <Button className="w-full" asChild>
                        <Link to="/contact">
                          Demander un devis
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services détaillés
            </h2>
            <p className="text-xl text-gray-600">
              Une gamme complète de services pour tous vos besoins de contrôle technique.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Contrôle technique périodique",
                description: "Voitures particulières, taxis, véhicules utilitaires - Service mobile à domicile",
                icon: Target
              },
              {
                title: "Pré-visite préventive",
                description: "Détection des points à corriger avant la visite officielle pour maximiser vos chances",
                icon: BarChart3
              },
              {
                title: "Rapport clair et transparent",
                description: "Documentation détaillée et explicite de tous les points contrôlés",
                icon: FileText
              },
              {
                title: "Contre-visite",
                description: "Vérification des réparations effectuées suite à un premier contrôle",
                icon: CheckCircle
              },
              {
                title: "Normes environnementales",
                description: "Vérification de la conformité aux normes environnementales et émissions",
                icon: Lightbulb
              },
              {
                title: "Suivi électronique",
                description: "Suivi électronique du carnet de contrôle pour un historique complet",
                icon: TrendingUp
              }
            ].map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <service.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-center">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Services complémentaires
            </h2>
            <p className="text-xl text-gray-600">
              Des expertises additionnelles pour répondre à tous vos besoins.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <service.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-center">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre processus
            </h2>
            <p className="text-xl text-gray-600">
              Une méthodologie éprouvée pour garantir votre succès.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Diagnostic", description: "Analyse approfondie de votre situation actuelle" },
              { step: "02", title: "Stratégie", description: "Élaboration d'un plan d'action personnalisé" },
              { step: "03", title: "Mise en œuvre", description: "Accompagnement dans l'exécution des solutions" },
              { step: "04", title: "Suivi", description: "Monitoring des résultats et ajustements" }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">{phase.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{phase.title}</h3>
                <p className="text-gray-600">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Prêt à transformer votre entreprise ?
            </h2>
            <p className="text-xl text-blue-100">
              Contactez-nous pour discuter de vos besoins et découvrir comment nos services 
              peuvent vous aider à atteindre vos objectifs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/contact">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <Link to="/about">En savoir plus</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;